/**
 * The `ivy-checkpoint` block a skill ends a stop with (packages/ivy-chat/CLAUDE.chat.md,
 * "Checkpoints in this environment"). The REST facade reads it for API callers; the chat
 * renders it as a card, and the answer it sends is the one the contract names: the option's
 * label, "Yes, go ahead.", or the text.
 */

export type CheckpointKind = 'choice' | 'confirm' | 'text';

export interface CheckpointOption {
  id: string;
  label: string;
  previewUrl?: string;
}

export interface Checkpoint {
  id: string;
  kind: CheckpointKind;
  question?: string;
  options?: CheckpointOption[];
  recommended: string;
  humanUrl?: string;
}

const KINDS: CheckpointKind[] = ['choice', 'confirm', 'text'];

export function parseCheckpointBlock(raw: string): Checkpoint | null {
  let v: any;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v.id !== 'string' || !KINDS.includes(v.kind) || typeof v.recommended !== 'string') return null;
  const options: CheckpointOption[] | undefined = Array.isArray(v.options)
    ? v.options
        .filter((o: any) => o && typeof o.id === 'string' && typeof o.label === 'string')
        .map((o: any) => ({ id: o.id, label: o.label, ...(typeof o.previewUrl === 'string' && { previewUrl: o.previewUrl }) }))
    : undefined;
  if (v.kind === 'choice' && !options?.length) return null;
  return {
    id: v.id,
    kind: v.kind,
    ...(typeof v.question === 'string' && { question: v.question }),
    ...(options && { options }),
    recommended: v.recommended,
    ...(typeof v.humanUrl === 'string' && { humanUrl: v.humanUrl }),
  };
}

export function checkpointAnswer(cp: Checkpoint, optionId?: string): string {
  if (cp.kind === 'confirm') return 'Yes, go ahead.';
  if (cp.kind === 'text') return cp.recommended;
  const id = optionId ?? cp.recommended;
  return cp.options?.find(o => o.id === id)?.label ?? id;
}

/** A link back to the page the user is already on adds nothing; anywhere else it does. */
export function showHumanUrl(humanUrl: string | undefined, currentHref: string): boolean {
  if (!humanUrl) return false;
  try {
    const target = new URL(humanUrl);
    const here = new URL(currentHref);
    return !(target.origin === here.origin && target.pathname.replace(/\/$/, '') === here.pathname.replace(/\/$/, ''));
  } catch {
    return false;
  }
}
